/* ==========================================================================
   BC cart item — ajax remove and refused-change messaging.

   RUNS ON BOTH SURFACES: the bin replaces the Remove link on the cart page
   as well as in the drawer.

   WHAT IT REPLACES. snippets/cart-item.liquid had the theme's only full-page
   navigation to /cart/change — a plain <a href> that reloaded the page to
   remove a line. That is a named suspect in the unresolved "no valid id or
   line parameter" checkout error, because the error page is a NAVIGATION to
   /cart/change and no .js fetch path can produce one. Replacing it retires
   the mechanism; it does not prove the cause.

   NO theme.js EDITS. Two of its behaviours have to be worked around instead,
   and both are load-bearing:

     1. CartForm.quantityChanged() starts `if (!key || !qty) return;` — and 0
        is falsy. The stepper can therefore never remove a line, which is why
        removal was a link in the first place. Quantity 0 is handled here.

     2. theme.cart._updateCart() never checks the HTTP status. It calls
        response.json() and returns whatever comes back, so a 422 body — which
        has no item_count — flows on as if it were a cart. Every request here
        goes through our own wrapper, which reads response.ok first.

   Rebuilds are handed back to the theme by dispatching `cart:build`, which
   CartForm listens for. That keeps ONE fetch per change and means the drawer
   and the cart page each refresh through their own existing machinery.
   ========================================================================== */

(function () {
  'use strict';

  var REMOVE = '[data-bc-remove]';
  var ROW = '.cart__item';
  var ERROR_CLASS = 'bc-item__error';
  var NOTE_CLASS = 'bc-item__note';

  function row(el) { return el && el.closest ? el.closest(ROW) : null; }

  /* ⚠ SHOWS SHOPIFY'S OWN MESSAGE, NEVER INVENTED COPY. A 422 body carries a
     `description` written and localised by Shopify — "You can only add 3 of
     this item to your cart", and so on. It is more accurate than anything we
     could write, because only Shopify knows the stock position. The locale
     string is a fallback for a network failure, where there is no body. */
  function showRowMessage(rowEl, message, className) {
    if (!rowEl || !message) return;
    var cls = className || ERROR_CLASS;
    var el = rowEl.querySelector('.' + ERROR_CLASS + ', .' + NOTE_CLASS);
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('role', 'alert');
      var details = rowEl.querySelector('.cart__item-details') || rowEl;
      details.appendChild(el);
    }
    /* Set every time, not only on create: one row can only be saying one of
       these at once, and a note must not inherit the error's red. */
    el.className = cls;
    el.textContent = message;
  }

  function showRowError(rowEl, message) {
    showRowMessage(rowEl, message, ERROR_CLASS);
  }

  function clearRowError(rowEl) {
    if (!rowEl) return;
    var el = rowEl.querySelector('.' + ERROR_CLASS + ', .' + NOTE_CLASS);
    if (el) el.parentNode.removeChild(el);
  }

  function genericError() {
    return (window.theme && theme.strings && theme.strings.cartUpdateError) ||
      'Sorry, we could not update your basket. Please try again.';
  }

  /* "Only 22 available". The string is a locale key read raw from
     theme.strings, so the {{ count }} placeholder is still in it and gets
     substituted here — the same shape the drawer's subtotal label uses. */
  function cappedNote(count) {
    var tpl = (window.theme && theme.strings && theme.strings.cartQuantityCapped) ||
      'Only {{ count }} available';
    return tpl.replace(/\{\{\s*count\s*\}\}/g, count);
  }

  /* ⚠ EVERY CART REQUEST GOES UNDER THE MARKET'S ROUTE ROOT — "/fr-fr/",
     "/en-us/" — never a bare /cart path. Shopify.routes.root is written by
     Shopify into the page head for the market serving it; "/" is the GB
     storefront. Read at call time, not cached at load. Stage 7 (28 September)
     found a quantity change on /fr-fr/ and /en-us/ breaking in the drawer;
     the same helper is repeated in bc-discount-form.js, bc-cart-fbt.js,
     bc-cart-drawer.js and pdp-test.js, which load independently of this file. */
  function cartRoute(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + path;
  }

  /* One request wrapper for every path here, and the one Stage 6 reuses for
     add failures. Resolves with {ok, status, body} rather than throwing, so
     the caller decides what a refusal means. */
  function postCartChange(key, quantity) {
    return fetch(cartRoute('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (res) {
      return res.json()
        .catch(function () { return {}; })
        .then(function (body) { return { ok: res.ok, status: res.status, body: body }; });
    });
  }

  /* ⚠ A RENDERED KEY CAN BE STALE, and it is why 27/28 failed where Rebuy's
     drawer did not (Stage 7, 28 September).

     A line's key includes its discount allocations, so when the basket is
     re-priced into another currency every DISCOUNTED line gets a new key.
     Measured on the build theme: a GB basket switched to /fr-fr/ took the
     Protective Cover from …218c296b (£19.35 off) to …6a451260 (€28.50 off),
     while the undiscounted Luna kept its key. Our rows carry the key Liquid
     wrote at the last render, so after a switch a change can name a line the
     basket no longer has, and Shopify answers 400 "no valid id or line
     parameter".

     Rebuy's drawer never met this: it sent keys from a /cart.js it had just
     read. So do we. Read the basket under the market root, send the key if it is
     still there, otherwise take the line at the row's position — but only
     when it is the same variant. Anything else is no match, and a guess could
     change the wrong line, so nothing is sent. */

  function rowIndex(rowEl) {
    if (!rowEl || !rowEl.parentNode) return -1;
    var rows = Array.prototype.filter.call(rowEl.parentNode.children, function (el) {
      return el.classList.contains('cart__item');
    });
    return rows.indexOf(rowEl);
  }

  /* Resolves with the key to send, or null for no clear match. A failed read
     is no match too: sending blind is what this exists to stop. */
  function resolveKey(key, index) {
    return fetch(cartRoute('cart.js'), { cache: 'no-store', credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('cart read ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          if (items[i].key === key) return key;
        }
        var variantId = parseInt(String(key).split(':')[0], 10);
        var line = index >= 0 ? items[index] : null;
        return line && line.variant_id === variantId ? line.key : null;
      })
      .catch(function () { return null; });
  }

  function setBusy(rowEl, busy) {
    if (!rowEl) return;
    var qty = rowEl.querySelector('.js-qty__wrapper');
    if (qty) qty.classList.toggle('is-loading', !!busy);
    rowEl.classList.toggle('bc-item--busy', !!busy);
  }

  /* ⚠ SCOPED TO THE ROW, NOT document.querySelector('[data-products]').
     On the cart page there are TWO of those: the drawer renders a hidden copy
     of the item list in the header, which comes FIRST in the DOM. A global
     query would return the drawer's hidden container — locking the wrong
     element, and, in rebuildThenReport below, watching a container that is
     never rebuilt on that template, so the message would never reappear. */
  function productsFor(rowEl) {
    return rowEl && rowEl.closest ? rowEl.closest('[data-products]') : null;
  }

  /* ⚠ THE FLASH IS THE SWAP, NOT A RELOAD.
     theme.js's cartMarkup() does `this.products.innerHTML = ''` and THEN
     appends the new list. Between those two statements the container is empty,
     so it collapses to zero height and the page jumps — for as long as the
     fetch takes. On a long cart page that reads exactly like a reload.

     Holding the container's height for the duration keeps the layout still,
     and a short fade makes the content change read as an update rather than a
     blink. Released on the next frame after the swap lands, so the container
     returns to its natural height in one reflow instead of two. */
  function holdHeight(products) {
    if (!products) return;

    products.style.minHeight = products.offsetHeight + 'px';
    products.classList.add('bc-cart-updating');

    function release() {
      window.requestAnimationFrame(function () {
        products.style.minHeight = '';
        products.classList.remove('bc-cart-updating');
      });
    }

    var observer = new MutationObserver(function () {
      observer.disconnect();
      release();
    });
    observer.observe(products, { childList: true });

    /* Never leave the layout pinned if the rebuild does not arrive. */
    setTimeout(function () { observer.disconnect(); release(); }, 5000);
  }

  function onCartPage() {
    return document.body.classList.contains('template-cart');
  }

  function changeQuantity(rowEl, key, quantity) {
    if (!key) return;

    var products = productsFor(rowEl);
    var index = rowIndex(rowEl);
    setBusy(rowEl, true);
    clearRowError(rowEl);

    resolveKey(key, index).then(function (fresh) {
      if (fresh) {
        sendChange(rowEl, fresh, quantity, index);
        return;
      }
      setBusy(rowEl, false);
      rebuildThenReport(products, key, genericError(), index);
    });
  }

  function sendChange(rowEl, key, quantity, index) {
    var products = productsFor(rowEl);
    setBusy(rowEl, true);
    clearRowError(rowEl);

    /* Held only where we are going to stay on the page. On the cart page a
       successful change reloads, so pinning the layout first would be wasted
       work on a document that is about to be replaced. */
    if (!onCartPage()) holdHeight(products);

    postCartChange(key, quantity).then(function (res) {
      setBusy(rowEl, false);

      if (!res.ok) {
        /* Refused — out of stock, or past the quantity limit. Show Shopify's
           own wording on the row and rebuild anyway, so the number returns to
           what the cart actually holds rather than the value the stepper
           optimistically painted. The error is re-shown after the rebuild
           because the rebuild replaces the row. */
        var message = res.body.description || res.body.message || genericError();
        rebuildThenReport(products, key, message, index);
        return;
      }

      /* ⚠ THE CART PAGE RELOADS AFTER EVERY SUCCESSFUL REMOVAL. Deliberate,
         and it matches what that page already does.

         Three reasons, in order of weight:

         1. cart:build only replaces [data-products], [data-discounts] and
            [data-subtotal]. Everything else on the cart page — the empty state,
            which sections/main-cart.liquid decides at PAGE LOAD inside
            `{% if cart.item_count > 0 %}`, plus EcoCart, Carma and the
            discount lines — is rendered once and never refreshed. A partial
            rebuild leaves those stale or, when the last line goes, leaves a
            live form with no empty-cart message.

         2. It is what the page already did. The Remove link this bin replaces
            was a full-page navigation, and the LIVE cart page today reloads on
            a stepper "+" as well — confirmed by test, cause not in the theme's
            own JS, so an app is doing it. Reloading here changes nothing a
            shopper has not already been getting.

         3. The earlier version reloaded only when item_count reached 0, and
            that condition fired with items still in the basket. I could not
            reproduce it: /cart/change.js returns the correct item_count when
            removing one of two lines, and a stale key returns HTTP 400 with no
            item_count at all, which lands on the !res.ok branch above. Rather
            than repair a condition whose failure mode I cannot explain, the
            condition is gone — the reload is now unconditional here.

         The DRAWER keeps the ajax rebuild and never reloads: it owns all of
         its own chrome, and its empty state is a class the rebuild sets. */
      if (onCartPage()) {
        window.location.reload();
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:build'));
    }).catch(function () {
      setBusy(rowEl, false);
      showRowError(rowEl, genericError());
    });
  }

  /* The rebuild replaces [data-products] wholesale, so a message written
     before it is thrown away. Wait for the row to come back, then write it. */
  function rebuildThenReport(products, key, message, index) {
    if (!products) return;

    var observer = new MutationObserver(function () {
      var rowEl = document.querySelector('[data-bc-remove="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
      rowEl = row(rowEl);
      /* The key may be the stale one that caused this, so fall back to the
         same position in the rebuilt list — only if it is the same variant,
         or the message would sit on a product it is not about. */
      if (!rowEl && index >= 0) {
        var at = Array.prototype.filter.call(products.querySelectorAll(ROW), function (el) {
          return el.parentNode.classList.contains('cart__items');
        })[index];
        var variant = String(key).split(':')[0];
        if (at && String(at.getAttribute('data-key') || '').split(':')[0] === variant) rowEl = at;
      }
      if (!rowEl) {
        /* No row to hang it on: the line has gone or moved. Say it once at
           the top of the rebuilt list, in the drawer and on the cart page
           alike. The next rebuild clears it with everything else. */
        if (typeof message !== 'string' || !products.querySelector('.cart__items')) return;
        observer.disconnect();
        var note = document.createElement('div');
        note.setAttribute('role', 'alert');
        note.className = ERROR_CLASS + ' bc-item__error--list';
        note.textContent = message;
        products.insertBefore(note, products.firstChild);
        return;
      }
      observer.disconnect();
      /* A FUNCTION IS ALLOWED, and stage 6 needs it: the capped-quantity note
         cannot be written until the rebuilt row is here to be read, because
         the number in it comes from that row. A plain string still works and
         is what every other caller passes. */
      if (typeof message === 'function') message(rowEl);
      else showRowError(rowEl, message);
    });
    observer.observe(products, { childList: true });

    /* Never leave an observer running if the row does not come back — the line
       may legitimately have gone. */
    setTimeout(function () { observer.disconnect(); }, 5000);

    document.dispatchEvent(new CustomEvent('cart:build'));
  }

  /* Delegated, so it survives every rebuild without re-binding. */
  document.addEventListener('click', function (evt) {
    var btn = evt.target && evt.target.closest ? evt.target.closest(REMOVE) : null;
    if (!btn) return;
    evt.preventDefault();
    changeQuantity(row(btn), btn.getAttribute('data-bc-remove'), 0);
  });

  /* QUANTITY 0 FROM THE STEPPER. theme.QtySelector dispatches
     `cart:quantity` + the CartForm namespace, so the event name carries it:
     cart:quantity.cart-header in the drawer, cart:quantity.cart-page on the
     cart page. theme.js's own handler bails on a falsy 0, so the two never
     collide — it ignores exactly the case handled here. */
  ['cart:quantity.cart-header', 'cart:quantity.cart-page'].forEach(function (name) {
    document.addEventListener(name, function (evt) {
      var detail = evt.detail || [];
      var key = detail[0];
      var qty = detail[1];
      if (!key || qty !== 0) return;
      var el = detail[2];
      changeQuantity(row(el), key, 0);
    });
  });

  /* ======================================================================
     STAGE 6 — a refused QUANTITY INCREASE says so, on its own row.

     THE FAULT. theme.js's _updateCart (:1926) never checks response.ok: it
     parses the body and hands it back whatever it is. On a 422 that body is
     {status, message, description} with NO item_count, so quantityChanged's
     `cart.item_count > 0` reads undefined > 0, takes the else branch, and adds
     .is-empty to the drawer. cartMarkup() then recomputes from data-count when
     buildCart() lands, so the empty state is a flash rather than a stuck
     state — and what the shopper is left with is the number snapping back with
     no explanation. The only other handling is `.catch(function(XMLHttpRequest){})`,
     which swallows everything else in silence.

     WHY A WRAPPER AND NOT AN EDIT. assets/theme.js is the file Isaac's open
     branches also touch, and a clean git merge of two edits to one file is
     exactly how this project has lost code before. Wrapping changes nothing in
     that file and lifts off in one line at stage 10.

     WHY IT REJECTS. Returning the body would let quantityChanged carry on and
     paint the empty flash. Rejecting instead lands in its own empty catch, so
     none of that runs — and because nothing runs, nothing rebuilds either,
     which is why the rebuild is ours: rebuildThenReport dispatches cart:build,
     waits for the row to come back and writes the message onto it. The same
     path the bin already uses for a refused removal, so there is one mechanism
     and one look for both.

     The stepper's own is-loading class goes with the old node when the rebuild
     replaces it. */
  /* The visible one: on the cart page the drawer's hidden copy of the list
     comes first in the DOM. */
  function rowForKey(key) {
    var btns = document.querySelectorAll(
      '[data-bc-remove="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]'
    );
    for (var i = 0; i < btns.length; i++) {
      if (btns[i].getClientRects().length) return row(btns[i]);
    }
    return row(btns[0]);
  }

  function wrapChangeItem() {
    if (!window.theme || !theme.cart || typeof theme.cart.changeItem !== 'function') return;
    if (theme.cart.bcQuantityWrapped) return;

    theme.cart.bcQuantityWrapped = true;
    var original = theme.cart.changeItem;

    theme.cart.changeItem = function (key, qty) {
      var self = this;
      var rowEl = rowForKey(key);
      var products = productsFor(rowEl);
      var index = rowIndex(rowEl);

      return resolveKey(key, index).then(function (fresh) {
        if (!fresh) {
          /* No clear match: nothing sent. Rejecting lands in quantityChanged's
             own empty catch, as a refusal does below. */
          if (products) {
            rebuildThenReport(products, key, genericError(), index);
          } else {
            document.dispatchEvent(new CustomEvent('cart:build'));
            if (window.bcCartDrawer) window.bcCartDrawer.showError(genericError());
          }
          return Promise.reject(new Error('bc: no matching line, nothing sent'));
        }
        return original.call(self, fresh, qty).then(function (cart) {
          return reportRefusal(cart, fresh);
        });
      });

      function reportRefusal(cart, key) {
        if (!cart || cart.status !== 422) return cart;

        var message = cart.description || genericError();

        if (products) {
          /* ⚠ SHOPIFY CLAMPS AND REFUSES AT THE SAME TIME, and that is not
             obvious from either half on its own. Measured against live,
             11 September, asking for 999 of a line with 22 in stock:

               POST /cart/change.js   -> HTTP 422
               body                   -> {status, message, description}
                                         no items, no item_count
               description            -> "Only 22 items were added to your
                                          cart due to availability."
               GET /cart.js after it  -> item_count 22

             So the refusal is real AND the cart moved. Reading the body alone
             would say nothing happened; reading the cart alone would say it
             succeeded. Both are needed to describe it.

             The body carries no quantity, so the number cannot come from it.
             It comes from the REBUILT ROW instead — theme.js's own
             /cart?view=ajax render — which is why this is deferred until the
             row is back rather than written now.

             When the line did move, "Only 22 available" is the shorter and
             plainer sentence, and it replaces Shopify's longer one rather
             than joining it: two messages for one event reads as two faults.
             When nothing moved — a genuine refusal, nothing added — the
             description stands exactly as before. */
          rebuildThenReport(products, key, function (freshRow) {
            var input = freshRow.querySelector('.js-qty__num');
            var actual = input ? parseInt(input.value, 10) : NaN;

            if (isFinite(actual) && actual > 0 && actual < qty) {
              showRowMessage(freshRow, cappedNote(actual), NOTE_CLASS);
              return;
            }

            showRowError(freshRow, message);
          }, index);
        } else {
          /* rebuildThenReport returns without dispatching when it has no
             container to observe, which would leave the stepper showing a
             number the cart refused. Rebuild anyway and put the message where
             it can still be read. */
          document.dispatchEvent(new CustomEvent('cart:build'));
          if (window.bcCartDrawer) window.bcCartDrawer.showError(message);
        }

        return Promise.reject(new Error('bc: quantity change refused'));
      }
    };
  }

  /* ⚠ THIS FILE LOADS BEFORE theme.js, AND THAT IS WHY THE FIRST VERSION OF
     THIS NEVER INSTALLED. Measured on the rendered page, 11 September:

       <script src=".../bc-cart-item.js" defer>   offset   775,282
       <script src=".../theme.js"        defer>   offset 1,593,409

     This one is loaded from snippets/header-cart-drawer.liquid, which renders
     in the header section; theme.js sits near the foot of the body. Deferred
     scripts execute in DOCUMENT ORDER, so this file runs first and theme.cart
     does not exist yet.

     The second half of the trap: a deferred script runs at readyState
     'interactive', never 'loading'. So the usual
     `readyState === 'loading' ? wait : go` shape — which is correct in every
     other file here, because none of the others needs theme.js — took the
     immediate branch, found no theme.cart, and returned. Nothing retried it.
     `theme.cart.bcQuantityWrapped` read undefined on the build theme and the
     stepper's 422 went on being silent.

     An earlier reading of the page said the order was the other way round. It
     matched a <link rel="preload" as="script"> for theme.js in the head rather
     than the script tag. Match the tag, not the filename.

     So: try now, in case theme.js has somehow already run; then again on
     DOMContentLoaded, which every deferred script is guaranteed to precede;
     then once more on load, for anything injected later. wrapChangeItem is
     idempotent — the bcQuantityWrapped guard is the whole point of it — so
     calling it three times costs one assignment. */
  wrapChangeItem();
  document.addEventListener('DOMContentLoaded', wrapChangeItem);
  window.addEventListener('load', wrapChangeItem);

  /* Exposed for Stage 6, which needs the same row messaging for refused ADDS
     and must not grow a second error mechanism to do it. */
  window.bcCartItem = {
    showRowError: showRowError,
    clearRowError: clearRowError,
    postCartChange: postCartChange,
    genericError: genericError
  };
})();
